import { Stack, TextField } from '@mui/material'
import { Modal, OrderDropdown, tokens, User } from 'ComponentLibrary'
import { ComponentProps, useState } from 'react'
import { ParticipantsAutocomplete } from 'ComponentLibrary/src/widgets/Participants/ParticipantsAutocomplete'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { BellIcon } from '@phosphor-icons/react'
import { InfoBox } from 'ComponentLibrary/src/piazza/info/InfoBox/InfoBox'
import { type OrderDropdownCopy } from 'ComponentLibrary/src/molecules/OrderDropdown/OrderDropdown'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type ParticipantsProps = {
    users: User[]
}

type CreateNewThreadProps = {
    open: boolean
    participantsProps: ParticipantsProps
    isCreatingThread: boolean | undefined
    onClose: () => void
    orderOptions?: OrderOption[]
    onSubmit: CreateThreadSubmitHandler
    defaultValues?: Pick<FormData, 'selectedOrder'>
    copy?: Partial<CreateNewThreadDialogCopy>
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

type OrderOption = ComponentProps<typeof OrderDropdown>['orders'][0]

type FormData = {
    topic: string
    participants: User[]
    selectedOrder: OrderOption | null
}

type ResetFormFn = () => void
export type CreateThreadSubmitHandler = (data: FormData, resetForm: ResetFormFn) => void

export type CreateNewThreadDialogCopy = {
    title: string
    create: string
    cancel: string
    topicLabel: string
    participantsLabel: string
    defaultNotifiedUsers: string
    yourself: string
    creator: string
    observers: string
    assignees: string
    observersDescriptionSuffix: string
    orderDropdown: OrderDropdownCopy
}

const DEFAULT_CREATE_NEW_THREAD_DIALOG_COPY: CreateNewThreadDialogCopy = {
    title: 'Neue Unterhaltung',
    create: 'Erstellen',
    cancel: 'Abbrechen',
    topicLabel: 'Titel *',
    participantsLabel: 'Weitere Benachrichtigte hinzufügen...',
    defaultNotifiedUsers: 'Standard-Benachrichtigte dieser neuen Unterhaltung:',
    yourself: 'Sie selbst',
    creator: 'Ersteller/in',
    observers: 'Beobachter/innen',
    assignees: 'Zuständige',
    observersDescriptionSuffix: 'des Auftrags, inkl.',
    orderDropdown: {
        loading: 'Lade Auftragsdaten...',
        label: 'Auftrag',
        noOptions: 'Keine Aufträge gefunden',
        valuationDate: 'Stichtag',
        orderDate: 'Auftragsdatum',
    },
}

export function CreateNewThreadDialog({
    open,
    onClose,
    participantsProps: { users },
    isCreatingThread = false,
    onSubmit,
    orderOptions,
    defaultValues,
    copy,
    userLinkCopy,
    locale = 'de-DE',
}: CreateNewThreadProps) {
    const resolvedCopy = { ...DEFAULT_CREATE_NEW_THREAD_DIALOG_COPY, ...copy }
    const orderDropdownCopy = { ...DEFAULT_CREATE_NEW_THREAD_DIALOG_COPY.orderDropdown, ...copy?.orderDropdown }
    const [topic, setTopic] = useState<string>('')
    const [participants, setParticipants] = useState<User[]>([])
    const [selectedOrder, setSelectedOrder] = useState<OrderOption | null>(
        orderOptions?.find((orderOption) => orderOption.uuid === defaultValues?.selectedOrder?.uuid) || null
    )

    const resetFormValues = () => {
        setTopic('')
        setParticipants([])
        setSelectedOrder(null)
    }

    const handleSubmit = () => {
        onSubmit({ topic, participants, selectedOrder }, resetFormValues)
    }

    const handleClose = () => {
        resetFormValues()
        onClose()
    }

    return (
        <Modal
            title={resolvedCopy.title}
            onClose={handleClose}
            disableBackdropClose
            showCloseIcon
            isOpen={open}
            footerProps={{
                primaryButton: {
                    text: resolvedCopy.create,
                    disabled: !topic || (orderOptions !== undefined && !selectedOrder) || isCreatingThread,
                    onClick: handleSubmit,
                    icon: isCreatingThread ? <LoadingIndicator isPending secondaryLoading={false} height={1.3} /> : undefined,
                },
                secondaryButton: { text: resolvedCopy.cancel, onClick: handleClose },
            }}
        >
            <Stack direction="column" gap={tokens.SizingSpacing150} width="100%">
                <TextField autoFocus fullWidth value={topic} label={resolvedCopy.topicLabel} onChange={(e) => setTopic(e.target.value)} />
                {orderOptions !== undefined && (
                    <OrderDropdown
                        orders={orderOptions}
                        filterValue={selectedOrder?.uuid || null}
                        setFilterValue={(newValue) =>
                            setSelectedOrder(orderOptions?.find((orderOption) => orderOption.uuid === newValue) || null)
                        }
                        size="medium"
                        required
                        copy={orderDropdownCopy}
                        locale={locale}
                    />
                )}
                <Stack direction="column" gap={tokens.SizingSpacing150}>
                    <ParticipantsAutocomplete
                        users={users.filter((user) => !user.isCurrentUser)}
                        newParticipants={participants}
                        setNewParticipants={setParticipants}
                        label={resolvedCopy.participantsLabel}
                        userLinkCopy={userLinkCopy}
                    />
                    <InfoBox variant="ordinary" icon={<BellIcon />}>
                        <Typography variant="textS" color={tokens.ColorTextBold}>
                            {resolvedCopy.defaultNotifiedUsers}
                            <ul style={{ padding: '0 24px', margin: 0 }}>
                                <li>
                                    <strong>{resolvedCopy.yourself}</strong> ({resolvedCopy.creator})
                                </li>
                                <li>
                                    <strong>{resolvedCopy.observers}</strong> {resolvedCopy.observersDescriptionSuffix}{' '}
                                    <strong>{resolvedCopy.assignees}</strong>
                                </li>
                            </ul>
                        </Typography>
                    </InfoBox>
                </Stack>
            </Stack>
        </Modal>
    )
}
